import { Skeleton } from '@monksflow/monks-ui';

/**
 * Animated placeholder shown while a Trackers board's items/briefs load.
 *
 * Mirrors the board's real shape — a group card with a header strip, a column
 * header row, and several table rows — so the swap to real content doesn't shift
 * layout or flash. Follows the app-wide loading convention (monks-ui `Skeleton`
 * shimmer that mimics the content), replacing the previous bare "Loading…" text.
 *
 * Purely decorative: the whole tree is `aria-hidden` and carries no data, so
 * screen readers announce the eventual content, not the placeholder.
 */

// Static, never-reordered placeholder slots — stable string keys so the list
// doesn't rely on array indexes as React keys.
const COLUMN_KEYS = ['c1', 'c2', 'c3', 'c4'] as const;
const DEFAULT_ROW_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'] as const;

interface TrackersBoardSkeletonProps {
  /** How many placeholder rows to render (defaults to 6). */
  rows?: number;
}

export function TrackersBoardSkeleton({ rows }: TrackersBoardSkeletonProps) {
  const rowKeys =
    rows === undefined
      ? DEFAULT_ROW_KEYS
      : Array.from({ length: Math.max(1, rows) }, (_, i) => `r${i}`);

  return (
    <div className="flex flex-col gap-6 pb-3" aria-hidden>
      <div className="overflow-hidden rounded-lg border border-border">
        {/* Group header strip: chevron + title + item count */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          <Skeleton className="size-4 rounded-sm" />
          <Skeleton className="h-4 w-32 rounded-sm" />
          <Skeleton className="ml-auto h-3 w-12 rounded-sm" />
        </div>

        {/* Column header row */}
        <div className="flex items-center gap-6 border-t border-border bg-background px-3 py-2.5">
          <Skeleton className="h-3 w-40 rounded-sm" />
          {COLUMN_KEYS.map((key) => (
            <Skeleton key={key} className="h-3 w-24 rounded-sm" />
          ))}
        </div>

        {/* Body rows */}
        {rowKeys.map((key) => (
          <div key={key} className="flex items-center gap-6 border-t border-border px-3 py-3">
            <Skeleton className="h-4 w-40 rounded-sm" />
            {COLUMN_KEYS.map((col) => (
              <Skeleton key={col} className="h-4 w-24 rounded-sm" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
